'use client'
// v2
import { useState, useEffect } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useTheme } from '@/contexts/ThemeContext'
import { getSupabaseClient } from '@/utils/supabase/singleton'
import { useAuthStore } from '@/store/useAuthStore'
import WelcomeModal, { checkShouldShowWelcome } from '@/components/WelcomeModal'

interface Tour {
  id: string
  title: string
  destination: string
  start_date: string | null
  end_date: string | null
  price: number | null
  max_participants: number | null
  current_participants: number | null
  thumbnail_url: string | null
  status: string
  max_depth?: number | null
  instructor_profiles?: {
    display_name: string
    is_verified: boolean
  } | null
}

interface Instructor {
  id: string
  display_name: string
  profile_image_url: string | null
  main_region: string
  certification_agency: string
  is_verified: boolean
  average_rating: number
  review_count: number
}

interface BuddyStats {
  totalInstructors: number
  todayBuddyCount: number
}


function SeatGauge({ current, max, urgent }: { current: number; max: number; urgent?: boolean }) {
  const pct = max > 0 ? Math.min((current / max) * 100, 100) : 0
  return (
    <div className="od-gauge-track w-full">
      <div
        className={`od-gauge-fill ${urgent ? 'urgent' : ''}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  )
}

function DepthMeter() {
  return (
    <div className="flex flex-col gap-1.5 items-end">
      <span className="block h-px bg-[#143560] opacity-55" style={{ width: 18 }} />
      <span className="block h-px bg-[#143560] opacity-40" style={{ width: 28 }} />
      <span className="block h-px bg-[#143560] opacity-30" style={{ width: 22 }} />
      <span className="block h-px bg-[#143560] opacity-20" style={{ width: 10 }} />
    </div>
  )
}

export default function HomePage() {
  const { theme } = useTheme()
  const isDark = theme === 'dark'
  const { user } = useAuthStore()
  const router = useRouter()

  const [tours, setTours] = useState<Tour[]>([])
  const [instructors, setInstructors] = useState<Instructor[]>([])
  const [stats, setStats] = useState<BuddyStats>({ totalInstructors: 0, todayBuddyCount: 0 })
  const [isLoadingTours, setIsLoadingTours] = useState(true)
  const [isLoadingInstructors, setIsLoadingInstructors] = useState(true)
  const [showWelcome, setShowWelcome] = useState(false)

  useEffect(() => {
    loadTours()
    loadInstructors()
    loadStats()
    if (checkShouldShowWelcome()) setShowWelcome(true)
  }, [])

  const loadTours = async () => {
    try {
      const supabase = getSupabaseClient()
      const { data } = await supabase
        .from('diving_tours')
        .select(`
          id, title, destination, start_date, end_date,
          price, max_participants, current_participants,
          thumbnail_url, status,
          instructor_profiles!instructor_id (display_name, is_verified)
        `)
        .eq('status', 'recruiting')
        .order('created_at', { ascending: false })
        .limit(4)
      setTours((data || []) as Tour[])
    } catch (e) {
      console.error(e)
    } finally {
      setIsLoadingTours(false)
    }
  }

  const loadInstructors = async () => {
    try {
      const supabase = getSupabaseClient()
      const { data } = await supabase
        .from('instructor_profiles')
        .select('id, display_name, profile_image_url, main_region, certification_agency, is_verified, average_rating, review_count')
        .neq('is_active', false)
        .order('created_at', { ascending: false })
        .limit(6)
      setInstructors(data || [])
    } catch (e) {
      console.error(e)
    } finally {
      setIsLoadingInstructors(false)
    }
  }

  const loadStats = async () => {
    try {
      const supabase = getSupabaseClient()
      const [{ count: instCount }, { count: buddyCount }] = await Promise.all([
        supabase.from('instructor_profiles').select('*', { count: 'exact', head: true }).neq('is_active', false),
        supabase.from('match_requests').select('*', { count: 'exact', head: true }).eq('date', new Date().toISOString().slice(0, 10)),
      ])
      setStats({ totalInstructors: instCount || 0, todayBuddyCount: buddyCount || 0 })
    } catch (e) {
      console.error(e)
    }
  }

  const formatPrice = (price: number | null) => {
    if (!price) return '가격 문의'
    return new Intl.NumberFormat('ko-KR').format(price) + '원'
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return ''
    const d = new Date(dateStr)
    return `${d.getMonth() + 1}/${d.getDate()}`
  }

  const bg = isDark ? '#0f172a' : '#f4f7fb'
  const ink = isDark ? '#e8f0fb' : '#0a1f3a'
  const secondary = isDark ? '#94a3b8' : '#6b7684'
  const card = isDark ? '#1e2a3d' : '#ffffff'
  const border = isDark ? '#2c3a52' : '#e9eff7'

  return (
    <div style={{ background: bg, minHeight: '100vh' }}>

      {/* ── 히어로 ── */}
      <section style={{ background: bg }} className="px-5 pt-10 pb-8 max-w-2xl mx-auto">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            {/* Wordmark */}
            <div className="flex items-center gap-2 mb-8">
              <span
                className="inline-block w-2.5 h-2.5 rounded-full"
                style={{ border: `1.5px solid ${isDark ? '#5ea6ff' : '#2b6df0'}`, boxShadow: `inset 0 -3px 0 rgba(43,109,240,0.18)` }}
              />
              <span style={{ font: '700 15px/1 sans-serif', color: ink, letterSpacing: '-0.02em' }}>
                오늘 다이빙
              </span>
            </div>

            <h1
              className="od-rise"
              style={{
                font: '700 30px/40px sans-serif',
                color: ink,
                letterSpacing: '-0.01em',
                margin: 0,
              }}
            >
              수면 아래는<br />
              <span style={{ color: isDark ? '#5ea6ff' : '#2b6df0' }}>언제나 고요해요.</span>
            </h1>

            <p style={{ font: '400 14px/22px sans-serif', color: secondary, marginTop: 12 }}>
              검증된 강사와 함께, 한 번의 숨으로<br />더 깊은 세상으로.
            </p>

            <div className="flex flex-wrap gap-2 mt-7">
              <Link
                href="/tours"
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl font-bold text-sm transition-all"
                style={{ background: isDark ? '#1e3a5f' : '#0a1f3a', color: '#fff' }}
              >
                투어 보기
              </Link>
              <Link
                href="/instructors"
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl font-bold text-sm transition-all"
                style={{ background: 'transparent', color: isDark ? '#5ea6ff' : '#2b6df0', border: `1px solid ${isDark ? '#2c3a52' : '#d6e4f7'}` }}
              >
                강사 찾기
              </Link>
              <Link
                href="/matching"
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl font-bold text-sm transition-all"
                style={{ background: 'transparent', color: isDark ? '#5ea6ff' : '#2b6df0', border: `1px solid ${isDark ? '#2c3a52' : '#d6e4f7'}` }}
              >
                버디 찾기
              </Link>
              <Link
                href="/map?tab=valley"
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl font-bold text-sm transition-all"
                style={{ background: 'transparent', color: isDark ? '#5ea6ff' : '#2b6df0', border: `1px solid ${isDark ? '#2c3a52' : '#d6e4f7'}` }}
              >
                계곡지도
              </Link>
            </div>
          </div>

          <div className="flex flex-col items-end gap-3 ml-4 mt-2">
            {isDark && <DepthMeter />}
          </div>
        </div>
      </section>

      {/* ── 스탯 타일 ── */}
      <section className="px-5 mb-8 max-w-2xl mx-auto">
        <div className="grid grid-cols-2 gap-3">
          <Link
            href="/instructors"
            className="rounded-2xl p-4 flex flex-col gap-1 transition-all"
            style={{ background: card, border: `1px solid ${border}` }}
          >
            <span style={{ font: '400 12px/18px sans-serif', color: secondary }}>검증된 강사</span>
            <span style={{ font: '700 22px/28px sans-serif', color: ink, fontVariantNumeric: 'tabular-nums' }}>
              {stats.totalInstructors > 0 ? `${stats.totalInstructors}명` : '—'}
            </span>
          </Link>
          <Link
            href="/matching"
            className="rounded-2xl p-4 flex flex-col gap-1 transition-all"
            style={{ background: card, border: `1px solid ${border}` }}
          >
            <span style={{ font: '400 12px/18px sans-serif', color: secondary }}>오늘 버디 매칭</span>
            <span style={{ font: '700 22px/28px sans-serif', color: ink, fontVariantNumeric: 'tabular-nums' }}>
              {stats.todayBuddyCount > 0 ? `${stats.todayBuddyCount}건` : '0건'}
            </span>
          </Link>
        </div>
      </section>


      {/* ── 최신 투어 ── */}
      <section className="px-5 mb-10 max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 style={{ font: '700 16px/24px sans-serif', color: ink }}>최신 투어</h2>
          <Link href="/tours" style={{ font: '500 13px/20px sans-serif', color: isDark ? '#5ea6ff' : '#2b6df0' }}>
            전체보기 →
          </Link>
        </div>

        {isLoadingTours ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="rounded-2xl p-4 animate-pulse" style={{ background: card, height: 88 }} />
            ))}
          </div>
        ) : tours.length === 0 ? (
          <div className="rounded-2xl p-10 text-center" style={{ background: card, border: `1px solid ${border}` }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>🌊</div>
            <p style={{ color: ink, font: '600 15px/22px sans-serif', marginBottom: 6 }}>아직 투어가 없습니다</p>
            <p style={{ color: secondary, font: '400 13px/20px sans-serif', marginBottom: 16 }}>강사님이 되어 첫 투어를 등록해보세요</p>
            <Link
              href="/for-instructors"
              style={{ display: 'inline-block', padding: '10px 20px', borderRadius: 12, background: '#2b6df0', color: '#fff', font: '600 14px/20px sans-serif', textDecoration: 'none' }}
            >
              강사 등록하기 →
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {tours.map(tour => {
              const filled = tour.current_participants || 0
              const total = tour.max_participants || 0
              const isUrgent = total > 0 && total - filled <= 2
              return (
                <Link
                  key={tour.id}
                  href={`/tours/${tour.id}`}
                  className="block rounded-2xl p-4 transition-all"
                  style={{ background: card, border: `1px solid ${border}` }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span
                          className="px-1.5 py-0.5 rounded text-xs font-medium"
                          style={{ background: isDark ? '#1e3a5f' : '#e9eff7', color: isDark ? '#5ea6ff' : '#143560', font: '500 11px/16px sans-serif' }}
                        >
                          {tour.destination}
                        </span>
                        {tour.max_depth && (
                          <span style={{ font: '400 11px/16px sans-serif', color: secondary }}>
                            {tour.max_depth}m
                          </span>
                        )}
                        {isUrgent && (
                          <span className="flex items-center gap-1">
                            <span className="od-rise-dot" style={{ position: 'static', animation: 'od-breath 2s ease-in-out infinite', width: 5, height: 5, background: '#0748ff', borderRadius: '50%', display: 'inline-block' }} />
                            <span style={{ font: '500 11px/16px sans-serif', color: '#0748ff' }}>임박</span>
                          </span>
                        )}
                      </div>
                      <p
                        className="line-clamp-1"
                        style={{ font: '500 14px/22px sans-serif', color: ink, margin: 0 }}
                      >
                        {tour.title}
                      </p>
                      {tour.start_date && (
                        <p style={{ font: '400 12px/18px sans-serif', color: secondary, margin: '2px 0 0' }}>
                          {formatDate(tour.start_date)}{tour.end_date ? ` ~ ${formatDate(tour.end_date)}` : ''}
                          {tour.instructor_profiles ? ` · ${tour.instructor_profiles.display_name}` : ''}
                        </p>
                      )}
                      {total > 0 && (
                        <div className="mt-2">
                          <SeatGauge current={filled} max={total} urgent={isUrgent} />
                          <p style={{ font: '400 11px/16px sans-serif', color: secondary, margin: '3px 0 0' }}>
                            {total - filled}자리 남음
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <span
                        className="od-breath"
                        style={{ font: '700 15px/22px sans-serif', color: isDark ? '#5ea6ff' : '#0748ff', fontVariantNumeric: 'tabular-nums' }}
                      >
                        {formatPrice(tour.price)}
                      </span>
                    </div>
                  </div>
                </Link>
              )
            })}
          </div>
        )}
      </section>

      {/* ── 강사 섹션 ── */}
      <section className="px-5 mb-10 max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 style={{ font: '700 16px/24px sans-serif', color: ink }}>강사 소개</h2>
          <Link href="/instructors" style={{ font: '500 13px/20px sans-serif', color: isDark ? '#5ea6ff' : '#2b6df0' }}>
            전체보기 →
          </Link>
        </div>

        {isLoadingInstructors ? (
          <div className="grid grid-cols-3 gap-2">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="rounded-2xl p-3 animate-pulse" style={{ background: card, height: 96 }} />
            ))}
          </div>
        ) : instructors.length === 0 ? (
          <p style={{ color: secondary, font: '400 14px/22px sans-serif' }}>등록된 강사가 없습니다</p>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {instructors.map(inst => (
              <Link
                key={inst.id}
                href={`/instructors/${inst.id}`}
                className="rounded-2xl p-3 flex flex-col items-center text-center gap-1.5 transition-all"
                style={{ background: card, border: `1px solid ${border}` }}
              >
                <div
                  className="w-12 h-12 rounded-full flex-shrink-0 overflow-hidden flex items-center justify-center text-lg font-bold"
                  style={{
                    background: isDark ? '#1e2d45' : '#d6e4f7',
                    color: isDark ? '#5ea6ff' : '#143560',
                    ...(inst.is_verified ? { boxShadow: `0 0 0 2px #fff, 0 0 0 3.5px ${isDark ? '#5ea6ff' : '#2b6df0'}` } : {}),
                  }}
                >
                  {inst.profile_image_url
                    ? <img src={inst.profile_image_url} alt={inst.display_name} className="w-full h-full object-cover" />
                    : inst.display_name.charAt(0)
                  }
                </div>
                <span style={{ font: '600 12px/18px sans-serif', color: ink }} className="line-clamp-1 w-full">
                  {inst.display_name}
                </span>
                <span style={{ font: '400 11px/16px sans-serif', color: secondary }}>
                  {inst.main_region}
                </span>
                {inst.average_rating > 0 && (
                  <span style={{ font: '500 11px/16px sans-serif', color: isDark ? '#fbbf24' : '#d97706', fontVariantNumeric: 'tabular-nums' }}>
                    ★ {inst.average_rating.toFixed(1)}
                  </span>
                )}
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* ── 버디 매칭 CTA ── */}
      <section className="px-5 mb-10 max-w-2xl mx-auto">
        <div
          className="relative overflow-hidden rounded-2xl p-6"
          style={{ background: isDark ? '#1e2a3d' : '#0a1f3a' }}
        >
          <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
            <span className="od-rise-dot" />
            <span className="od-rise-dot" />
            <span className="od-rise-dot" />
          </div>
          <div className="relative">
            <p style={{ font: '400 12px/18px sans-serif', color: 'rgba(255,255,255,0.6)', marginBottom: 4 }}>버디가 필요하신가요?</p>
            <h3 style={{ font: '700 18px/26px sans-serif', color: '#fff', margin: '0 0 8px' }}>
              같은 날, 같은 바다
            </h3>
            <p style={{ font: '400 13px/20px sans-serif', color: 'rgba(255,255,255,0.7)', margin: '0 0 16px' }}>
              같은 장소에서 다이빙할 버디를 찾아보세요.
            </p>
            <div className="flex gap-2">
              <Link
                href="/matching/new"
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold transition-all"
                style={{ background: '#2b6df0', color: '#fff' }}
              >
                버디 구하기
              </Link>
              <Link
                href="/matching"
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold transition-all"
                style={{ background: 'rgba(255,255,255,0.12)', color: '#fff' }}
              >
                목록 보기
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── 강사 등록 CTA ── */}
      <section className="px-5 pb-16 max-w-2xl mx-auto">
        <Link
          href="/for-instructors"
          className="block rounded-2xl p-5 transition-all"
          style={{ background: card, border: `1px solid ${border}` }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p style={{ font: '400 12px/18px sans-serif', color: secondary, marginBottom: 2 }}>강사님이신가요?</p>
              <p style={{ font: '700 15px/22px sans-serif', color: ink, margin: 0 }}>
                수수료 <span style={{ color: '#0748ff' }} className="od-breath">0원</span>으로 시작하기
              </p>
            </div>
            <span style={{ font: '500 13px/20px sans-serif', color: isDark ? '#5ea6ff' : '#2b6df0' }}>→</span>
          </div>
        </Link>
      </section>

      <WelcomeModal isOpen={showWelcome} onClose={() => setShowWelcome(false)} />
    </div>
  )
}
